/**
 * Start a Review run: the draft (an uploaded file OR one document from a
 * collection) and the reference collection(s) to check it against. Creates a
 * `solution_run` of kind `review`; the backend re-checks every access.
 *
 * A collection draft is picked as collection → document (a plain select of the
 * collection's indexed documents), and while the form is incomplete the button
 * says what is still missing — a disabled button with no reason is a dead end.
 */

import { useEffect, useState } from "react";
import { Button, Group, SegmentedControl, Select, Stack, Text, TextInput } from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  CollectionDocumentPicker,
  SolutionUpload,
  type CollectionSelection,
} from "@/features/solutions/shared";
import { getIndex, type IndexedDocumentOut } from "@/shared/api/indexes";
import { createRun, type RunDetailOut, type UploadRefOut } from "@/shared/api/solutionRuns";
import { toastApiError } from "@/shared/ui/toast";
import { type DraftSource, missingForReview } from "./reviewMissing";

interface Props {
  onCreated: (run: RunDetailOut) => void;
}

function useIndexedDocuments(indexId: string | null): IndexedDocumentOut[] | null {
  const getToken = useAccessToken();
  const [docs, setDocs] = useState<IndexedDocumentOut[] | null>(null);
  useEffect(() => {
    setDocs(null);
    if (!indexId) return;
    let alive = true;
    void (async () => {
      try {
        const detail = await getIndex(await getToken(), indexId);
        if (alive) setDocs(detail.documents.filter((d) => d.status === "indexed"));
      } catch (err) {
        toastApiError("Could not load the collection's documents", err);
        if (alive) setDocs([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [getToken, indexId]);
  return docs;
}

export function ReviewForm({ onCreated }: Props) {
  const getToken = useAccessToken();
  const [name, setName] = useState("");
  const [source, setSource] = useState<DraftSource>("upload");
  const [upload, setUpload] = useState<UploadRefOut | null>(null);
  const [draftCollection, setDraftCollection] = useState<CollectionSelection[]>([]);
  const [draftDocumentId, setDraftDocumentId] = useState<string | null>(null);
  const [references, setReferences] = useState<CollectionSelection[]>([]);
  const [busy, setBusy] = useState(false);

  const draftCollectionId = draftCollection[0]?.indexId ?? null;
  const draftDocs = useIndexedDocuments(source === "document" ? draftCollectionId : null);

  // A different collection invalidates the document picked from the last one.
  useEffect(() => setDraftDocumentId(null), [draftCollectionId]);

  const missing = missingForReview({
    name,
    source,
    hasUpload: upload !== null,
    draftCollectionId,
    draftDocumentId,
    referenceCount: references.length,
  });
  const ready = missing.length === 0 && !busy;

  const start = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      const run = await createRun(token, {
        kind: "review",
        name: name.trim(),
        collections: references.map((c) => ({ index_id: c.indexId, document_ids: c.documentIds })),
        upload_ids: source === "upload" && upload ? [upload.upload_id] : [],
        params:
          source === "document" && draftCollectionId && draftDocumentId
            ? { draft_document: { index_id: draftCollectionId, document_id: draftDocumentId } }
            : {},
      });
      onCreated(run);
    } catch (err) {
      toastApiError("Could not start the review", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md" data-testid="review-form">
      <TextInput
        label="Name"
        placeholder="e.g. Clinical overview draft v3"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />
      <Stack gap={6}>
        <Text size="sm" fw={500}>
          Draft to check
        </Text>
        <SegmentedControl
          value={source}
          onChange={(v) => setSource(v as DraftSource)}
          aria-label="Draft source"
          data={[
            { value: "upload", label: "Upload a file" },
            { value: "document", label: "A document in a collection" },
          ]}
        />
        {source === "upload" ? (
          <SolutionUpload value={upload} onChange={setUpload} label="Draft file" />
        ) : (
          <>
            <CollectionDocumentPicker
              value={draftCollection}
              onChange={setDraftCollection}
              label="Collection holding the draft"
              multiple={false}
              allowDocumentSubset={false}
            />
            {draftCollectionId && (
              <Select
                label="Draft document"
                placeholder={draftDocs === null ? "Loading documents…" : "Choose the draft"}
                data={(draftDocs ?? []).map((d) => ({
                  value: d.document_id,
                  label: d.display_name,
                }))}
                value={draftDocumentId}
                onChange={setDraftDocumentId}
                disabled={draftDocs === null}
                nothingFoundMessage="No indexed documents in this collection"
                searchable
                required
              />
            )}
          </>
        )}
      </Stack>
      <CollectionDocumentPicker
        value={references}
        onChange={setReferences}
        label="Reference collections (the sources to check against)"
      />
      <Group justify="flex-end" align="center" gap="sm">
        {missing.length > 0 && (
          <Text size="xs" c="dimmed" data-testid="review-missing">
            To start: {missing.join(" · ")}
          </Text>
        )}
        <Button onClick={() => void start()} disabled={!ready} loading={busy}>
          Start review
        </Button>
      </Group>
    </Stack>
  );
}
